import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { coerceBooleanProperty } from "@angular/cdk/coercion";

export type BvButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type BvButtonSize = "sm" | "md" | "lg";

/**
 * Attribute-selector button. Apply `bv-button` to a native `<button>` or
 * `<a>` so click semantics, type, disabled, routerLink, focus all work
 * naturally on the host element.
 *
 *   <button bv-button variant="primary" (click)="save()">Save</button>
 *   <a bv-button variant="ghost" routerLink="/foo">Browse</a>
 *   <button bv-button variant="ghost" iconOnly><bv-icon name="close"></bv-icon></button>
 */
@Component({
    selector: "button[bv-button], a[bv-button]",
    template: "<ng-content></ng-content>",
    styleUrls: ["./button.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "bv-button",
        "[class.bv-button--primary]": 'variant === "primary"',
        "[class.bv-button--secondary]": 'variant === "secondary"',
        "[class.bv-button--ghost]": 'variant === "ghost"',
        "[class.bv-button--danger]": 'variant === "danger"',
        "[class.bv-button--sm]": 'size === "sm"',
        "[class.bv-button--md]": 'size === "md"',
        "[class.bv-button--lg]": 'size === "lg"',
        "[class.bv-button--block]": "block",
        "[class.bv-button--icon-only]": "iconOnly",
    }
})
export class BvButtonComponent {
  @Input() variant: BvButtonVariant = "primary";
  // sm is the app-wide default; reserve lg for prominent standalone CTAs
  // (e.g. empty-state "get started" buttons). md is rarely used.
  @Input() size: BvButtonSize = "sm";
  @Input() block = false;

  /**
   * Square, circular button sized for a single icon — replaces
   * `mat-icon-button`. Pair with `variant="ghost"` for the standard
   * toolbar/kebab look, `variant="danger"` for destructive icon
   * actions. Accepts the standalone-attribute form (`iconOnly`).
   */
  @Input()
  get iconOnly(): boolean { return this._iconOnly; }
  set iconOnly(v: any) { this._iconOnly = coerceBooleanProperty(v); }
  private _iconOnly = false;
}
